/**
 * `help/useHelpGuide.ts` — open an in-app guide from anywhere.
 *
 * The top bar's `<HelpMenu/>` owns the guide drawer. Any other surface
 * (a section's **?**, an empty state) asks for a guide with
 * `openGuide("roster")`; the menu listens with `useGuideOpenRequests` and
 * opens the drawer on that guide. Mirrors the tour's `openTour()` bridge.
 */
import { useEffect, useRef } from "react";

const OPEN_GUIDE_EVENT = "tarii:open-help-guide";

/** Ask the help menu to open the guide with this id. */
export function openGuide(id: string): void {
  window.dispatchEvent(new CustomEvent<string>(OPEN_GUIDE_EVENT, { detail: id }));
}

/** Call `onOpen(id)` whenever something asks for a guide. */
export function useGuideOpenRequests(onOpen: (id: string) => void): void {
  const ref = useRef(onOpen);
  ref.current = onOpen;
  useEffect(() => {
    const handler = (event: Event) => {
      const id = (event as CustomEvent<unknown>).detail;
      if (typeof id === "string") ref.current(id);
    };
    window.addEventListener(OPEN_GUIDE_EVENT, handler);
    return () => window.removeEventListener(OPEN_GUIDE_EVENT, handler);
  }, []);
}
